// Primitivos de UI (Tailwind). Accesibles, sin dependencias externas.
import type { ButtonHTMLAttributes, InputHTMLAttributes, JSX, ReactNode, SelectHTMLAttributes, TextareaHTMLAttributes } from "react";
import { useEffect, useRef } from "react";
import { Inbox } from "lucide-react";

type Variant = "primary" | "secondary" | "ghost" | "danger";

export function Button(
  { variant = "secondary", small, className, children, ...rest }:
  ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant; small?: boolean },
): JSX.Element {
  const base = variant === "primary" ? "btn-primary"
    : variant === "danger" ? "btn-danger"
    : variant === "ghost" ? "btn-ghost" : "btn-secondary";
  return (
    <button className={`${base} ${small ? "btn-sm" : ""} ${className ?? ""}`} {...rest}>
      {children}
    </button>
  );
}

export function Field(
  { label, error, required, children, hint }:
  { label?: string | undefined; error?: string | undefined; required?: boolean | undefined; children: ReactNode; hint?: string | undefined },
): JSX.Element {
  return (
    <div>
      {label && (
        <label className="label">
          {label}{required && <span className="text-danger"> *</span>}
        </label>
      )}
      {children}
      {hint && !error && <p className="mt-1 text-xs text-ink-muted">{hint}</p>}
      {error && <p className="mt-1 text-xs text-danger" style={{ animation: "rise 200ms var(--ease-out)" }}>{error}</p>}
    </div>
  );
}

export function Input(props: InputHTMLAttributes<HTMLInputElement>): JSX.Element {
  return <input {...props} className={`input ${props.className ?? ""}`} />;
}

export function Textarea(props: TextareaHTMLAttributes<HTMLTextAreaElement>): JSX.Element {
  return <textarea {...props} className={`input ${props.className ?? ""}`} />;
}

export function Select(
  { children, ...props }: SelectHTMLAttributes<HTMLSelectElement>,
): JSX.Element {
  return <select {...props} className={`input ${props.className ?? ""}`}>{children}</select>;
}

export function Card({ children, className }: { children: ReactNode; className?: string }): JSX.Element {
  return <div className={`card ${className ?? ""}`}>{children}</div>;
}

export function Badge({ children, color = "gray" }: { children: ReactNode; color?: string }): JSX.Element {
  const styles: Record<string, string> = { gray: "chip-gris", blue: "chip-azul", green: "chip-verde", amber: "chip-ambar", red: "chip-rojo", purple: "chip-morado" };
  return <span className={`badge chip ${styles[color] ?? styles["gray"]}`}>{children}</span>;
}

// Estados "activos" cuyo punto pulsa suavemente.
const PULSE_LABELS = new Set(["Nuevo", "Intentando contactar", "Contactado"]);

/** Badge que usa un color hex arbitrario (etapas, estados con color de la spec). */
export function DotBadge({ label, hex }: { label: string; hex?: string | null | undefined }): JSX.Element {
  const pulse = PULSE_LABELS.has(label);
  return (
    <span className="badge bg-slate-100 dark:bg-slate-800 text-ink-secondary">
      <span className={`w-2 h-2 rounded-full mr-1.5 ${pulse ? "pulse-dot" : ""}`} style={{ backgroundColor: hex ?? "#9ca3af" }} />
      {label}
    </span>
  );
}

export function Spinner({ className }: { className?: string }): JSX.Element {
  return (
    <svg className={`animate-spin ${className ?? "w-5 h-5 text-ink-muted"}`} viewBox="0 0 24 24" fill="none">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
    </svg>
  );
}

export function Loading({ label }: { label?: string }): JSX.Element {
  return (
    <div className="flex items-center justify-center gap-3 py-16 text-ink-muted">
      <Spinner /> <span className="text-sm">{label ?? "Cargando…"}</span>
    </div>
  );
}

/** Bloque skeleton con shimmer. */
export function Skeleton({ className }: { className?: string }): JSX.Element {
  return <div className={`shimmer rounded-md ${className ?? "h-4 w-full"}`} />;
}

/** Skeleton con forma de tabla (filas x columnas). */
export function TableSkeleton({ rows = 8, cols = 5 }: { rows?: number; cols?: number }): JSX.Element {
  return (
    <div className="p-2">
      {Array.from({ length: rows }).map((_, r) => (
        <div key={r} className="flex items-center gap-4 px-2 py-3 border-b border-line last:border-0">
          {Array.from({ length: cols }).map((_, c) => (
            <Skeleton key={c} className={`h-3.5 ${c === 0 ? "w-40" : "flex-1"}`} />
          ))}
        </div>
      ))}
    </div>
  );
}

export function ErrorState({ message, onRetry }: { message?: string; onRetry?: () => void }): JSX.Element {
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
      <div className="text-sm text-red-600">{message ?? "Ocurrió un error."}</div>
      {onRetry && <Button small onClick={onRetry}>Reintentar</Button>}
    </div>
  );
}

export function EmptyState(
  { title, body, action }: { title: string; body?: string; action?: ReactNode },
): JSX.Element {
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-20 text-center">
      <div className="relative w-20 h-20 grid place-items-center">
        <div className="absolute inset-0 rounded-2xl bg-primary-soft rotate-6" />
        <div className="absolute inset-0 rounded-2xl bg-surface border border-line -rotate-3" />
        <Inbox size={30} strokeWidth={1.5} className="relative text-primary" />
      </div>
      <div className="font-medium text-ink">{title}</div>
      {body && <div className="text-sm text-ink-muted max-w-sm">{body}</div>}
      {action && <div className="mt-2">{action}</div>}
    </div>
  );
}

/**
 * Panel de formulario EN LÍNEA (no flotante): se renderiza dentro del flujo de la
 * página, no como overlay. Mantiene la misma firma que antes para ser reemplazo
 * directo en toda la plataforma. Al abrirse, se desplaza a la vista.
 */
export function Modal(
  { open, onClose, title, children, footer, wide }:
  { open: boolean; onClose: () => void; title: string; children: ReactNode; footer?: ReactNode; wide?: boolean },
): JSX.Element | null {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    // Llevar la vista al inicio del formulario (no al centro).
    ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    const onKey = (e: KeyboardEvent): void => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div
      ref={ref}
      className={`card w-full ${wide ? "max-w-4xl" : "max-w-2xl"} scroll-mt-20 ring-1 ring-primary-100`}
      role="region"
      aria-label={title}
    >
      <div className="flex items-center justify-between px-5 py-3.5 border-b border-line">
        <h3 className="font-semibold text-ink">{title}</h3>
        <button className="btn-ghost btn-sm" onClick={onClose} aria-label="Cerrar">✕</button>
      </div>
      <div className="p-5">{children}</div>
      {footer && <div className="flex justify-end gap-2 px-5 py-3.5 border-t border-line bg-canvas rounded-b-lg">{footer}</div>}
    </div>
  );
}
